import { useEffect, useCallback } from 'react';

function isMobile() {
  if (typeof window === 'undefined') return false;
  const ua = navigator.userAgent || '';
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(ua) || window.innerWidth <= 768;
}

async function requestFullscreen() {
  const el = document.documentElement;
  if (document.fullscreenElement || document.webkitFullscreenElement) return;
  try {
    if (el.requestFullscreen) {
      await el.requestFullscreen();
    } else if (el.webkitRequestFullscreen) {
      await el.webkitRequestFullscreen();
    } else if (el.msRequestFullscreen) {
      await el.msRequestFullscreen();
    }
  } catch (e) {
    // Browsers often block automatic fullscreen; user can still toggle manually.
  }
}

async function exitFullscreen() {
  if (!document.fullscreenElement && !document.webkitFullscreenElement) return;
  try {
    if (document.exitFullscreen) {
      await document.exitFullscreen();
    } else if (document.webkitExitFullscreen) {
      await document.webkitExitFullscreen();
    } else if (document.msExitFullscreen) {
      await document.msExitFullscreen();
    }
  } catch (e) {
    // ignore
  }
}

export default function useFullscreen({ auto = true } = {}) {
  const request = useCallback(() => requestFullscreen(), []);
  const exit = useCallback(() => exitFullscreen(), []);
  const toggle = useCallback(() => {
    if (document.fullscreenElement || document.webkitFullscreenElement) {
      exit();
    } else {
      request();
    }
  }, [request, exit]);

  useEffect(() => {
    if (!auto) return;
    if (!isMobile()) return;

    // Attempt on mount (may be blocked until a user gesture).
    requestFullscreen();

    // Retry on the first user gesture.
    const tryOnInteraction = () => {
      requestFullscreen();
    };
    document.addEventListener('touchstart', tryOnInteraction, { once: true });
    document.addEventListener('click', tryOnInteraction, { once: true });
    document.addEventListener('keydown', tryOnInteraction, { once: true });

    // Re-enter fullscreen after orientation changes on mobile.
    const tryOnOrientation = () => {
      setTimeout(requestFullscreen, 300);
    };
    window.addEventListener('orientationchange', tryOnOrientation);

    return () => {
      document.removeEventListener('touchstart', tryOnInteraction);
      document.removeEventListener('click', tryOnInteraction);
      document.removeEventListener('keydown', tryOnInteraction);
      window.removeEventListener('orientationchange', tryOnOrientation);
    };
  }, [auto]);

  return { request, exit, toggle };
}
